import { computed, ref, unref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEscClose } from 'dashboard/composables/useEscClose';
import {
  buildMediaTabs,
  groupMediaByType,
  MEDIA_TYPES,
  normalizeMediaItems,
} from 'dashboard/components-next/gallery/galleryUtils';
import { normalizeSection } from '../utils/imovelShowFormatters';

/**
 * Galeria, lightbox e mapa do imóvel, sincronizados com a URL (?foto e :section).
 *
 * @param {object} options
 * @param {import('vue').Ref<object>} options.imovel
 * @param {import('vue').Ref<boolean>} options.isLoading
 * @param {import('vue').Ref<string>} options.activeSection
 * @param {Function} options.goToSection
 */
export function useImovelGallery({
  imovel,
  isLoading,
  activeSection,
  goToSection,
}) {
  const route = useRoute();
  const router = useRouter();

  const galleryTab = ref('fotos');
  const midiaIndex = ref(0);
  const lightboxOpen = ref(false);
  const lightboxTab = ref(MEDIA_TYPES.PHOTOS);

  const mediaList = computed(() => unref(imovel)?.midias || []);
  const galleryItems = computed(() =>
    normalizeMediaItems(mediaList.value).map((item, index) => ({
      ...item,
      alt:
        item.alt || `${unref(imovel)?.titulo || 'Imóvel'} - mídia ${index + 1}`,
    }))
  );
  const mediaGroups = computed(() => groupMediaByType(galleryItems.value));
  const mediaTabs = computed(() => buildMediaTabs(mediaGroups.value));
  const galleryTabs = computed(() => [
    ...mediaTabs.value,
    { id: 'mapa', label: 'Mapa' },
  ]);
  const activeGalleryItems = computed(
    () => mediaGroups.value[galleryTab.value] || []
  );
  const galleryEmptyMessage = computed(
    () =>
      ({
        [MEDIA_TYPES.PHOTOS]: 'Sem fotos disponíveis',
        [MEDIA_TYPES.VIDEO]: 'Ainda não há vídeo cadastrado para este imóvel.',
        [MEDIA_TYPES.VIDEO_360]: 'Ainda não há tour 360° cadastrado.',
        [MEDIA_TYPES.BLUEPRINT]: 'Ainda não há planta cadastrada.',
      })[galleryTab.value] || 'Sem mídias disponíveis'
  );

  const routeFotoQuery = computed(() => {
    const value = Array.isArray(route.query.foto)
      ? route.query.foto[0]
      : route.query.foto;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });

  const replaceFotoQuery = value => {
    const query = { ...route.query };
    if (value) {
      query.foto = String(value);
    } else {
      delete query.foto;
    }
    router.replace({ query }).catch(() => {});
  };

  const mapUrl = computed(() => {
    const current = unref(imovel);
    if (current?.latitude && current?.longitude) {
      return `https://www.google.com/maps?q=${current.latitude},${current.longitude}&z=15&output=embed`;
    }

    const query = [current?.endereco, current?.bairro, current?.cidade]
      .filter(Boolean)
      .join(', ');
    return query
      ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`
      : null;
  });

  const openLightbox = ({ index = 0 } = {}) => {
    const tab =
      galleryTab.value === 'mapa' ? MEDIA_TYPES.PHOTOS : galleryTab.value;
    const items = mediaGroups.value[tab] || [];
    if (!items.length) return;

    lightboxTab.value = tab;
    midiaIndex.value = Math.max(0, Math.min(index, items.length - 1));
    lightboxOpen.value = true;
  };

  const openGalleryTab = tab => {
    galleryTab.value = tab;
    openLightbox({ index: 0 });
  };

  const handleLightboxChange = payload => {
    midiaIndex.value = payload.index;
    lightboxTab.value = payload.tab || lightboxTab.value;
    galleryTab.value = lightboxTab.value;
  };

  const openLightboxFromFotoQuery = () => {
    if (
      !routeFotoQuery.value ||
      !mediaGroups.value[MEDIA_TYPES.PHOTOS]?.length
    ) {
      return;
    }

    galleryTab.value = MEDIA_TYPES.PHOTOS;
    lightboxTab.value = MEDIA_TYPES.PHOTOS;
    openLightbox({ index: routeFotoQuery.value - 1 });
  };

  useEscClose(lightboxOpen, () => {
    lightboxOpen.value = false;
  });

  watch(
    mediaTabs,
    tabs => {
      if (galleryTab.value === 'mapa') return;
      if (!tabs.some(tab => tab.id === galleryTab.value)) {
        galleryTab.value = tabs[0]?.id || MEDIA_TYPES.PHOTOS;
      }
    },
    { immediate: true }
  );

  watch([lightboxOpen, midiaIndex, lightboxTab], () => {
    if (lightboxOpen.value && lightboxTab.value === MEDIA_TYPES.PHOTOS) {
      const nextFoto = midiaIndex.value + 1;
      if (routeFotoQuery.value !== nextFoto) replaceFotoQuery(nextFoto);
      return;
    }

    if (!lightboxOpen.value && routeFotoQuery.value) {
      replaceFotoQuery(null);
    }
  });

  watch(
    () => route.query.foto,
    () => {
      if (!unref(isLoading) && !lightboxOpen.value) {
        openLightboxFromFotoQuery();
      }
    }
  );

  watch(
    () => route.params.section,
    value => {
      const section = normalizeSection(value);
      if (galleryTabs.value.some(tab => tab.id === section)) {
        galleryTab.value = section;
      }
    },
    { immediate: true }
  );

  watch(galleryTab, value => {
    if (!galleryTabs.value.some(tab => tab.id === value)) return;
    if (unref(activeSection) === value) return;
    if (unref(activeSection) === 'fotos' && value === 'fotos') return;

    goToSection(value);
  });

  return {
    galleryTab,
    midiaIndex,
    lightboxOpen,
    lightboxTab,
    galleryItems,
    mediaTabs,
    galleryTabs,
    activeGalleryItems,
    galleryEmptyMessage,
    mapUrl,
    openLightbox,
    openGalleryTab,
    handleLightboxChange,
    openLightboxFromFotoQuery,
  };
}

export default useImovelGallery;
